import { lazy } from 'react';
import type { ComponentType } from 'react';

const MAX_RETRY_COUNT = 3;
const RETRY_DELAY = 1500;

// Track failed chunks to avoid infinite retry loops
const failedChunks = new Set<string>();

export function lazyWithRetry<T extends ComponentType<unknown>>(
  componentImport: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const importFunc = async (retryCount = 0): Promise<{ default: T }> => {
      try {
        const component = await componentImport();

        // Clear failed chunks on successful import
        if (retryCount > 0) {
          failedChunks.clear();
        }

        return component;
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const chunkId = errorMessage;

        // Check if this is a chunk loading error
        const isChunkError =
          chunkId.includes('Loading chunk') ||
          chunkId.includes('Failed to fetch dynamically imported module') ||
          chunkId.includes('Failed to import') ||
          chunkId.includes('Loading CSS chunk');

        if (!isChunkError) {
          throw error;
        }

        // Avoid infinite loops for persistently failing chunks
        if (failedChunks.has(chunkId) && retryCount >= MAX_RETRY_COUNT) {
          console.error(
            `Chunk persistently failing after ${MAX_RETRY_COUNT} retries:`,
            chunkId
          );

          // Try one last time with page reload
          if (
            window.confirm('The application needs to be updated. Reload now?')
          ) {
            window.location.reload();
          }
          throw error;
        }

        if (retryCount < MAX_RETRY_COUNT) {
          console.warn(
            `Retrying chunk import (${retryCount + 1}/${MAX_RETRY_COUNT}):`,
            chunkId
          );

          // Add to failed chunks after first failure
          if (retryCount === 0) {
            failedChunks.add(chunkId);
          }

          // Clear module cache if available
          if ('webpackChunkName' in componentImport) {
            // @ts-expect-error - webpack specific
            delete __webpack_require__.cache[componentImport];
          }

          // Wait before retrying
          await new Promise((resolve) =>
            setTimeout(resolve, RETRY_DELAY * (retryCount + 1))
          );

          return importFunc(retryCount + 1);
        }

        // Max retries exceeded
        console.error(
          `Failed to load chunk after ${MAX_RETRY_COUNT} retries:`,
          chunkId
        );

        // Attempt to clear service worker cache
        if (
          'serviceWorker' in navigator &&
          navigator.serviceWorker.controller
        ) {
          navigator.serviceWorker.controller.postMessage({
            type: 'CLEAR_CACHE',
          });
        }

        throw error;
      }
    };

    return importFunc();
  });
}

// Helper to preload critical routes
export function preloadRoute(componentImport: () => Promise<unknown>) {
  try {
    componentImport();
  } catch (error) {
    console.warn('Failed to preload route:', error);
  }
}

// Clear stale caches on version mismatch
export async function clearStaleAssets() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    const staleCaches = cacheNames.filter(
      (name) => name.includes('workbox-precache') || name.includes('assets')
    );

    await Promise.all(staleCaches.map((name) => caches.delete(name)));
  }
}
